import React, { useState } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eye, MousePointer, ShoppingCart, CheckCircle, ArrowDown } from "lucide-react";
import { useAttributionJourney } from '@/hooks/useAttributionJourney';
import { format } from 'date-fns';

// Sample lead ID - in production, would have a selector
const SAMPLE_LEAD_ID = null; // Set to null to show placeholder

const actionIcons = {
  view: Eye,
  click: MousePointer,
  engagement: ShoppingCart,
  lead: CheckCircle,
  purchase: CheckCircle,
};

const stageColors = {
  awareness: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  consideration: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200',
  conversion: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
};

export default function AttributionJourney() {
  const { data: journey, isLoading } = useAttributionJourney(SAMPLE_LEAD_ID);

  if (!SAMPLE_LEAD_ID || !journey) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-muted-foreground">Select a lead to view their attribution journey</p>
          <p className="text-sm text-muted-foreground mt-2">
            In production, this would show the complete customer journey from first touch to purchase
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">
              Lead: {journey.lead_name} 
              {journey.became_customer && (
                <Badge variant="default" className="ml-2">Customer</Badge>
              )}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Product: {journey.product_name} • Value: ${journey.deal_value.toLocaleString()}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">{journey.attribution_window_days} days</p>
            <p className="text-xs text-muted-foreground">from first touch to purchase</p>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* First & Last Touch Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm font-medium text-muted-foreground mb-2">First Touch</p>
            <p className="font-semibold text-foreground">{journey.first_touch.campaign}</p>
            <Badge variant="outline" className="mt-2">{journey.first_touch.funnel_stage}</Badge>
          </div>
          
          <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-sm font-medium text-muted-foreground mb-2">Last Touch</p>
            <p className="font-semibold text-foreground">{journey.last_touch.campaign}</p>
            <Badge variant="outline" className="mt-2">{journey.last_touch.funnel_stage}</Badge>
          </div>
        </div>

        {/* Journey Timeline */}
        <div className="space-y-4">
          <h3 className="font-semibold text-foreground">Timeline ({journey.journey.length} touchpoints)</h3>
          
          <div className="space-y-3">
            {journey.journey.map((touchpoint, index) => {
              const Icon = actionIcons[touchpoint.action_type] || Eye;
              const stageColor = stageColors[touchpoint.funnel_stage] || stageColors.awareness;
              
              return (
                <div key={index} className="relative">
                  {index < journey.journey.length - 1 && (
                    <div className="absolute left-[19px] top-10 h-full w-0.5 bg-border" />
                  )}
                  
                  <div className="flex items-start gap-4">
                    <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 border-2 border-primary flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    
                    <div className="flex-1 pt-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-foreground">{touchpoint.touchpoint_channel || 'Campaign'}</p>
                        <Badge variant="outline" className={stageColor}>
                          {touchpoint.funnel_stage}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {touchpoint.action_type} • {safeFormat(new Date(touchpoint.created_at), 'PPp')}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Purchase Event */}
            {journey.became_customer && (
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500 border-2 border-green-600 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 pt-1">
                  <p className="font-semibold text-green-600 dark:text-green-400">Purchased! 🎉</p>
                  <p className="text-sm text-muted-foreground">
                    Value: ${journey.deal_value.toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Insight */}
        <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
          <p className="text-sm font-medium text-foreground">
            💡 This customer needed ALL {new Set(journey.journey.map(t => t.funnel_stage)).size} funnel stages to convert!
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Total touches: {journey.journey.length} • Spend attributed: ${(journey.deal_value / 3).toFixed(2)} (estimated)
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
